import type { DraggableProvidedDragHandleProps } from '@hello-pangea/dnd'
import { cn } from '@/lib/utils'
import TaskStructureBadges from '@/frontend/features/Fde/TaskStructureBadges'
import {
  TaskCommentCount,
  TaskDueDate,
  TaskPeople,
  TaskPriority,
  TaskStatusIcon,
  taskCreatedDate,
  taskStatusTone,
} from '@/frontend/features/Fde/projectTaskPresentation'
import type { FdeCopy, ProjectTask } from '@/frontend/features/Fde/types'

interface Props {
  task: ProjectTask
  overdue: boolean
  onSelect: () => void
  copy: FdeCopy
  dragHandleProps?: DraggableProvidedDragHandleProps | null
  nested?: boolean
}

export default function ProjectTaskRow({
  task,
  overdue,
  onSelect,
  copy,
  dragHandleProps,
  nested = false,
}: Props) {
  const created = copy.management.task_created_at.replace(
    '%{date}',
    taskCreatedDate(task.created_at),
  )
  const creator = copy.management.task_created_by.replace('%{name}', task.created_by_label)

  return (
    <button
      {...dragHandleProps}
      type="button"
      aria-label={task.title}
      onClick={onSelect}
      className={cn(
        'flex min-h-12 min-w-0 flex-1 items-center gap-2.5 px-3 py-2 text-left transition-colors hover:bg-muted/40 focus-visible:outline focus-visible:outline-2 focus-visible:outline-ring sm:gap-3 sm:px-4 sm:py-0',
        dragHandleProps && 'cursor-grab active:cursor-grabbing',
      )}
    >
      <TaskPriority
        priority={task.priority}
        label={`${copy.management.task_priority}: ${copy.management.priorities[task.priority]}`}
        iconOnly
      />
      <span
        title={task.identifier}
        className="hidden w-20 shrink-0 font-mono text-[10px] tracking-wide text-muted-foreground md:block"
      >
        {task.identifier}
      </span>
      <span
        title={copy.management.statuses[task.status]}
        className={cn('shrink-0', taskStatusTone[task.status].split(' ')[0])}
      >
        <TaskStatusIcon status={task.status} className="size-3.5" />
      </span>
      <span
        title={task.title}
        className={cn(
          'line-clamp-2 min-w-0 flex-1 text-[13px] leading-5 sm:block sm:truncate',
          nested ? 'font-normal' : 'font-medium',
        )}
      >
        {task.title}
      </span>
      <span className="hidden lg:inline-flex">
        <TaskStructureBadges task={task} copy={copy} showParent={!nested} />
      </span>
      <span className="hidden w-9 shrink-0 justify-end sm:flex">
        <TaskCommentCount count={task.comments.length} label={copy.management.comments} />
      </span>
      <time
        dateTime={task.created_at}
        title={`${created} · ${creator}`}
        className="hidden w-36 shrink-0 truncate text-[11px] text-muted-foreground xl:block"
      >
        {created}
      </time>
      <span className="flex w-20 shrink-0 justify-end">
        <TaskDueDate value={task.due_on} overdue={overdue} label={copy.management.task_due_on} />
      </span>
      <span className="flex w-14 shrink-0 justify-end">
        <TaskPeople
          people={task.assignees}
          emptyLabel={copy.management.task_unassigned}
          workStateLabels={copy.management.task_work_states}
          compact
        />
      </span>
    </button>
  )
}
